import * as THREE from 'three';

/**
 * Re-keys a model version's scan composition onto a new primary scan, rebasing every other scan's
 * transform so it remains in the same world position relative to the new primary's frame.
 *
 * Geometry: the version's scans currently live in the old primary's local frame, with the primary
 * itself at identity. When the user picks scan B (a supplementary) to become the new primary, B's
 * frame becomes the version's reference frame. Every scan's matrix has to be expressed in B's
 * frame instead of the old primary's — that's `inverse(B.oldTransform) * scan.oldTransform`,
 * which collapses to identity for B itself (so B sits at identity, as a primary should).
 *
 * Pure function over plain transform objects (the TransformViewModel shape:
 * `{ position: {x,y,z}, rotation: {x,y,z,order?}, scale: {x,y,z} }`). Three.js does the math here
 * but the inputs and outputs are plain JS so it composes with API payloads cleanly.
 */

const IDENTITY = Object.freeze({
  position: Object.freeze({ x: 0, y: 0, z: 0 }),
  rotation: Object.freeze({ isEuler: true, x: 0, y: 0, z: 0, order: 'XYZ' }),
  scale: Object.freeze({ x: 1, y: 1, z: 1 }),
});

export function identityTransform() {
  // Deep-clone so callers can mutate without corrupting the frozen template.
  return JSON.parse(JSON.stringify(IDENTITY));
}

export function transformToMatrix(t) {
  const m = new THREE.Matrix4();
  const position = t?.position ?? {};
  const rotation = t?.rotation ?? {};
  const scale = t?.scale ?? {};

  m.compose(
    new THREE.Vector3(position.x ?? 0, position.y ?? 0, position.z ?? 0),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(
      rotation.x ?? 0,
      rotation.y ?? 0,
      rotation.z ?? 0,
      rotation.order ?? 'XYZ',
    )),
    new THREE.Vector3(scale.x ?? 1, scale.y ?? 1, scale.z ?? 1),
  );
  return m;
}

export function matrixToTransform(m) {
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();
  m.decompose(position, quaternion, scale);
  const euler = new THREE.Euler().setFromQuaternion(quaternion, 'XYZ');

  return {
    position: { x: position.x, y: position.y, z: position.z },
    rotation: { isEuler: true, x: euler.x, y: euler.y, z: euler.z, order: 'XYZ' },
    scale: { x: scale.x, y: scale.y, z: scale.z },
  };
}

/**
 * Rebases a single scan's transform onto a new primary's frame:
 *   `scan.newTransform = inverse(newPrimary.oldTransform) * scan.oldTransform`.
 *
 * Returns the scan-relative-to-new-primary transform in the TransformViewModel shape.
 */
export function rebaseTransform(scanOldTransform, newPrimaryOldTransform) {
  const M_scan = transformToMatrix(scanOldTransform);
  const M_newPrim = transformToMatrix(newPrimaryOldTransform);
  const inv = new THREE.Matrix4().copy(M_newPrim).invert();
  const M_new = new THREE.Matrix4().multiplyMatrices(inv, M_scan);
  return matrixToTransform(M_new);
}

/**
 * Rekeys a list of source scans (the primary + supplementaries of an existing version) onto a new
 * primary, dropping any scans the caller chose to remove and returning the composition ready for
 * a fresh CreateModelVersionModel. The new primary's transform is identity (by definition of
 * "primary"), every other kept scan is rebased into the new primary's frame, and trim radii
 * travel along with their owning scans.
 *
 * sourceScans: [{ scanId, transform, trimRadius }] — typically built from a ModelVersionViewModel's
 * `scans` array (which already projects primary + supplementaries into a single uniform list with
 * the primary at identity and per-scan trim radii populated).
 *
 * keepScanIds: the subset of sourceScans to carry over (must include newPrimaryScanId).
 *
 * Returns: { primaryScanId, primaryScanTrimRadius, supplementaryScans } where
 * supplementaryScans are ScanAssociation-shaped objects ready to drop into the create payload.
 */
export function rebaseCompositionOnNewPrimary({ sourceScans, newPrimaryScanId, keepScanIds }) {
  const byId = new Map(sourceScans.map(s => [s.scanId, s]));
  const newPrimary = byId.get(newPrimaryScanId);
  if (!newPrimary) {
    throw new Error(`rebaseCompositionOnNewPrimary: new primary "${newPrimaryScanId}" is not in sourceScans`);
  }

  const keepSet = new Set(keepScanIds);
  keepSet.add(newPrimaryScanId);  // primary must always be kept; tolerate callers that forgot

  const newPrimaryOldTransform = newPrimary.transform ?? identityTransform();

  const supplementaryScans = sourceScans
    .filter(s => s.scanId !== newPrimaryScanId && keepSet.has(s.scanId))
    .map(s => ({
      scanId: s.scanId,
      transform: rebaseTransform(s.transform ?? identityTransform(), newPrimaryOldTransform),
      trimRadius: Number(s.trimRadius) || 0,
    }));

  return {
    primaryScanId: newPrimaryScanId,
    primaryScanTrimRadius: Number(newPrimary.trimRadius) || 0,
    supplementaryScans,
  };
}
